import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../user.service';
import { SettingsService } from '../settings.service';
import { HuntingDistrictService } from '../hunting-district.service';
import { UserFeedbackService } from '../user-feedback.service';
import { resolveNotificationContextPrefs } from '../utils/utils';

interface NotificationEventDef {
  key: string;
  labelKey: string;
}


const NOTIFICATION_EVENTS: NotificationEventDef[] = [
  { key: 'context-news-gameRecord', labelKey: 'FORM.userForm.notification_event.context-news-gameRecord' },
  { key: 'user-new-work-order',     labelKey: 'FORM.userForm.notification_event.user-new-work-order' },
];


@Component({
  selector: 'app-user-settings-dialog',
  templateUrl: './user-settings-dialog.component.html',
  styleUrls: ['./user-settings-dialog.component.css'],
})
export class UserSettingsDialogComponent implements OnInit {

  form!: FormGroup;
  saving = false;
  loading = true;
  allCountries: any[] = [];
  readonly notificationEvents = NOTIFICATION_EVENTS;

  private user: any = null;
  private contextId: string | null = null;

  get showNotificationPreferences(): boolean {
    const context = this.settingsService.selectedUserContext$?.value as any;
    return !!context?.modulesMap?.get('diwima-adis');
  }

  constructor(
    private dialogRef: MatDialogRef<UserSettingsDialogComponent>,
    private fb: FormBuilder,
    private userService: UserService,
    private settingsService: SettingsService,
    private huntingDistrictService: HuntingDistrictService,
    private userFeedbackService: UserFeedbackService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.contextId = this.settingsService.selectedUserContext$?.value?.identifier ?? null;

    this.allCountries = this.huntingDistrictService.allCountries$.value
      ? Object.values(this.huntingDistrictService.allCountries$.value)
      : [];
    if (this.allCountries.length) {
      this.allCountries.sort((a: any, b: any) => a?.name?.localeCompare(b.name));
    } else {
      this.huntingDistrictService.getCountriesList();
    }
    this.huntingDistrictService.allCountries$.subscribe(value => {
      this.allCountries = value ? Object.values(value).sort((a: any, b: any) => a?.name?.localeCompare(b.name)) : [];
    });

    this.userService.user$.subscribe(user => {
      this.user = user;
      this.loading = false;
      this.buildForm();
    });
  }

  private buildForm(): void {
    const u = this.user ?? {};
    const legalAddress = u.legalAddress ?? {};
    const contextPrefs = resolveNotificationContextPrefs(u, this.contextId);

    const group: Record<string, any> = {
      firstName:   [u.firstName ?? '', Validators.required],
      lastName:    [u.lastName ?? '', Validators.required],
      gender:      [u.gender ?? ''],
      salutation:  [u.salutation ?? ''],
      title:       [u.title ?? ''],
      telephone:   [u.telephone ?? ''],
      address1:    [legalAddress.address1 ?? ''],
      address2:    [legalAddress.address2 ?? ''],
      zip:         [legalAddress.zip ?? ''],
      city:        [legalAddress.city ?? ''],
      countryCode: [legalAddress.countryCode ?? ''],
      country:     [legalAddress.country ?? ''],
    };

    for (const event of this.notificationEvents) {
      const channels: string[] = contextPrefs[event.key]?.channels ?? [];
      // 'mail' is the old stored value; keep reading it so previously-saved prefs still show correctly.
      group[this.mailControlName(event)] = [channels.includes('email') || channels.includes('mail')];
      group[this.pushControlName(event)] = [channels.includes('push')];
    }

    this.form = this.fb.group(group);

    this.form.get('countryCode')?.valueChanges.subscribe(code => {
      const country = this.allCountries?.find((c: any) => c.isoCode === code);
      this.form.patchValue({ country: country?.name ?? '' }, { emitEvent: false });
    });
  }

  mailControlName(event: NotificationEventDef): string { return `${event.key}_mail`; }
  pushControlName(event: NotificationEventDef): string { return `${event.key}_push`; }

  cancel(): void {
    this.dialogRef.close();
  }

  async save(): Promise<void> {
    if (!this.form || this.form.invalid || this.saving || !this.user) return;
    this.saving = true;

    try {
      const v = this.form.getRawValue();

      const legalAddress = {
        address1:    v.address1 || undefined,
        address2:    v.address2 || undefined,
        zip:         v.zip || undefined,
        city:        v.city || undefined,
        country:     v.country || undefined,
        countryCode: v.countryCode || undefined,
      };

      const patch: Record<string, any> = {
        firstName:  v.firstName,
        lastName:   v.lastName,
        gender:     v.gender || undefined,
        salutation: v.salutation || undefined,
        title:      v.title || undefined,
        telephone:  v.telephone || undefined,
        legalAddress,
      };

      if (this.showNotificationPreferences && this.contextId) {
        const contextPrefs: Record<string, { channels: string[] }> = {};
        for (const event of this.notificationEvents) {
          const channels: string[] = [];
          if (v[this.mailControlName(event)]) channels.push('email');
          if (v[this.pushControlName(event)]) channels.push('push');
          if (channels.length) contextPrefs[event.key] = { channels };
        }

        // Merge onto the full map — other contexts' preferences are untouched.
        const merged: Record<string, any> = { ...(this.user.pushNotifications ?? {}) };
        if (Object.keys(contextPrefs).length) merged[this.contextId] = contextPrefs;
        else delete merged[this.contextId];

        patch.pushNotifications = merged;
      }

      await this.userService.updateOwnUser(this.user, patch);
      const msg = await this.translateService.get('changes_saved').toPromise();
      this.userFeedbackService.showSnackBar.emit(msg);
      this.dialogRef.close(true);
    } catch (err: any) {
      console.error('User settings save error', err);
      const m = this.translateService.instant('error_saving');
      this.userFeedbackService.showSnackBar.emit(err?.message ?? m);
    } finally {
      this.saving = false;
    }
  }
}